/**
 * Pure helpers for approved-sender input normalization, validation, and
 * display. Shared between onboarding (`StepApproveSenders`) and settings
 * (`SettingsApprovedSenders`).
 *
 * UI convention: domains are displayed with a leading `@` (`@school.org`),
 * emails as `local@host`. Storage is host-only for domains — see
 * `addApprovedSender` and the `webhooks-gmail-watch` edge function.
 */

import { isSharedBusinessPlatform } from '@/constants/senderPlatforms';

export interface MatchableSuggestion {
  type: 'domain' | 'email';
  /** Host for `domain` suggestions, full address for `email` suggestions. */
  value: string;
}

/** Lowercased host with any leading `@` stripped. */
export function normalizeDomainHost(input: string): string {
  return input.trim().replace(/^@/, '').toLowerCase();
}

/** Adds `@` if not present. */
export function prefixAt(input: string): string {
  const t = input.trim();
  return t.startsWith('@') ? t : `@${t}`;
}

/** Returns the canonical UI form `@host` for a domain string. */
export function formatDomainDisplay(input: string): string {
  return prefixAt(normalizeDomainHost(input));
}

/**
 * `true` if the input looks like a domain (starts with `@`, or is a bare host
 * with no local part). Returns `false` for full email addresses.
 *
 * Shape-only — the caller still runs full validation before adding.
 */
export function isDomainShape(input: string): boolean {
  const t = input.trim();
  if (t.startsWith('@')) return true;
  // bare host: no leading '@' and no '@' anywhere = domain shape
  if (!t.includes('@')) return true;
  return false;
}

/**
 * Returns the suggestion matching `input`, ignoring case:
 *   - a domain-shaped input (`@host` or bare host) matches a `domain`
 *     suggestion whose `value` host is equal;
 *   - an email-shaped input (local-part present) matches an `email`
 *     suggestion whose `value` address is equal.
 *
 * A domain-shaped input never matches an `email` suggestion (and vice versa):
 * the two key spaces are distinct.
 */
export function findMatchingSuggestion<T extends MatchableSuggestion>(
  input: string,
  suggestions: readonly T[],
): T | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  if (isDomainShape(trimmed)) {
    const host = normalizeDomainHost(trimmed);
    if (!host) return undefined;
    return suggestions.find(
      (s) => s.type === 'domain' && s.value.toLowerCase() === host,
    );
  }
  const addr = trimmed.toLowerCase();
  return suggestions.find(
    (s) => s.type === 'email' && s.value.toLowerCase() === addr,
  );
}

export interface SenderValidation {
  valid: boolean;
  type: 'domain' | 'email';
  /**
   * Storage-ready value when valid: bare host for `domain`, lowercased
   * email for `email`. This is what should be passed to `addApprovedSender`.
   */
  storageValue?: string;
  /** Canonical UI form when valid (`@host` for domain, raw email for email). */
  display?: string;
  error?: string;
  /**
   * Set on an input that is VALID but broader than the parent likely intends —
   * an address on a platform many unrelated businesses share. The add is
   * allowed; the caller should show this alongside the confirmation.
   *
   * Mirrors what onboarding does for the same senders: suggest, state the
   * breadth, and never pre-check. YAR-427 was a parent approving
   * `@pushpress.com` without being told it admitted every other tenant.
   */
  warning?: string;
}

/**
 * Validate and normalize a raw sender input string. Returns both a
 * `storageValue` (bare host for domains) and a `display` form (`@host`).
 *
 * Use `storageValue` when calling `addApprovedSender`. Use `display` for UI.
 */
export function validateSender(input: string): SenderValidation {
  const trimmed = input.trim();
  if (!trimmed) {
    return { valid: false, type: 'domain', error: 'Enter a sender' };
  }

  if (isDomainShape(trimmed)) {
    const host = normalizeDomainHost(trimmed);
    if (host.includes('.') && host.length > 1) {
      // The YAR-427 shape, refused at the only place it can still be typed.
      // A whole-domain approval on a platform many businesses share admits
      // every one of them that ever emails this household, and onboarding
      // never suggests it — so Settings should not accept it either.
      // The address form is still available, and is what the parent wants:
      // it names the sender they actually see.
      if (isSharedBusinessPlatform(host)) {
        return {
          valid: false,
          type: 'domain',
          error:
            `${prefixAt(host)} is shared by many businesses — approving it ` +
            `would let all of them in. Add the sender's full address instead.`,
        };
      }
      return {
        valid: true,
        type: 'domain',
        storageValue: host,
        display: prefixAt(host),
      };
    }
    return {
      valid: false,
      type: 'domain',
      error: 'Domain must contain a dot (e.g. @school.org)',
    };
  }

  // Email shape: has a local-part before `@`.
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (emailRegex.test(trimmed)) {
    const lower = trimmed.toLowerCase();
    const host = lower.slice(lower.lastIndexOf('@') + 1);
    return {
      valid: true,
      type: 'email',
      storageValue: lower,
      display: lower,
      // Allowed, but said out loud: on these platforms the sending address is
      // often shared by every tenant, so this can be as broad as the domain.
      // Onboarding shows the same thing rather than hiding the sender.
      ...(isSharedBusinessPlatform(host) && {
        warning:
          `${host} is shared by many businesses. If they send from a common ` +
          `address, approving this may let others through too.`,
      }),
    };
  }

  return {
    valid: false,
    type: 'email',
    error: 'Enter a valid email (teacher@school.org) or domain (@school.org)',
  };
}
